'use client';

import React, { useEffect, useRef, useState } from 'react';
import type { VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn/cn';
import { dialogVariants } from './Dialog.variants';

export type DialogProps = React.HTMLAttributes<HTMLDialogElement> &
  VariantProps<typeof dialogVariants> & {
    isOpen: boolean;
    /** Callback fired on dialog close */
    onClose: () => void;
  };

/**
 * A dialog implementing the visual layout of the empiriecom design system
 */
const Dialog = ({
  children,
  isOpen = false,
  onClose,
  variant = 'default',
  className,
  ...props
}: DialogProps) => {
  const ref = useRef<HTMLDialogElement>(null);
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  // Outlives isOpen by the length of the exit animation, because close() hides the dialog at once.
  const [isPresent, setIsPresent] = useState(isOpen);
  if (isOpen && !isPresent) {
    setIsPresent(true);
  }

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) {
      return undefined;
    }

    if (isOpen) {
      // A dialog reopened during its exit animation is still open, and older Safari throws on showModal() then.
      if (!dialog.open) {
        dialog.showModal();
      }
      return undefined;
    }

    let isInterrupted = false;
    const finishClosing = () => {
      if (!isInterrupted) {
        dialog.close();
        setIsPresent(false);
      }
    };

    // The stylesheet decides whether `data-closing` animates. Without { subtree: true }, since
    // that would wait for looping animations of the content, which never finish.
    const exitAnimations = dialog.getAnimations?.() ?? [];
    if (exitAnimations.length === 0) {
      finishClosing();
      return undefined;
    }

    // allSettled: `finished` rejects when the browser cancels the animation, e.g. on a breakpoint change.
    Promise.allSettled(exitAnimations.map((animation) => animation.finished)).then(finishClosing);
    return () => {
      isInterrupted = true;
    };
  }, [isOpen]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      className={cn(dialogVariants({ variant, isOpen: isPresent }), className)}
      ref={ref}
      data-closing={!isOpen && isPresent ? '' : undefined}
      onCancel={(event) => {
        // The native close on Escape would skip the exit animation and leave isOpen stale.
        event.preventDefault();
        onClose();
      }}
      // Handle not closing the dialog if its size changes and the mouse is on the backdrop
      onMouseDown={(e: React.MouseEvent<HTMLDialogElement>) => {
        mouseDownTargetRef.current = e.target;
      }}
      onClick={(event: React.MouseEvent<HTMLDialogElement>) => {
        if (event.target === ref.current) {
          const originalTarget = mouseDownTargetRef.current;
          if (originalTarget === ref.current) {
            onClose();
          }
        }
        mouseDownTargetRef.current = null;
      }}
      {...props}
    >
      {isPresent && children}
    </dialog>
  );
};

Dialog.displayName = 'Dialog';

export { Dialog };
